import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaCalendarCheck,
    FaQrcode,
    FaUserCheck,
    FaUserTimes,
    FaRegFileAlt,
    FaSearch,
    FaFilter,
    FaChevronRight,
    FaCheckCircle,
    FaHistory,
    FaTimes,
    FaRegCalendarCheck
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import EmptyState from '../../shared/EmptyState';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css';

const TutorAttendance = () => {
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedCourseId) {
            fetchRoster();
        }
    }, [selectedCourseId, selectedDate]);

    const fetchInitialData = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/tutor/courses', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const relevantCourses = res.data.filter(c => c.status === 'Active' || c.status === 'Completed');
            setCourses(relevantCourses);
            if (relevantCourses.length > 0) {
                setSelectedCourseId(relevantCourses[0]._id);
            } else {
                setLoading(false);
            }
        } catch (err) {
            showError('Initialization Error', 'Could not load your assigned batches.');
            setLoading(false);
        }
    };

    const fetchRoster = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/tutor/attendance/roster?courseId=${selectedCourseId}&date=${selectedDate}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRoster(res.data);
        } catch (err) {
            showError('Sync Error', 'Failed to retrieve batch roster.');
        } finally {
            setLoading(false);
        }
    };

    const markAttendance = async (studentId, status) => {
        showLoading(`Logging ${status}...`);
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/tutor/attendance/log', {
                courseId: selectedCourseId,
                date: selectedDate,
                records: [{ studentId, status }],
                type: 'Offline' // Manual entry defaults to offline context
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Optimistic Update
            setRoster(prev => prev.map(s => s.id === studentId ? { ...s, status } : s));
            closeLoading();
            showSuccess('Record Verified', `${status} status successfully committed to ledger.`);
        } catch (err) {
            closeLoading();
            showError('Update Failed', 'Could not sync attendance change with server.');
        }
    };

    const handleGenerateQR = () => {
        showLoading('Generating Secure Session QR...');
        setTimeout(() => {
            closeLoading();
            setIsQrModalOpen(true);
        }, 800);
    };

    const handleExport = () => {
        if (!roster || roster.length === 0) {
            showError('Export Failed', 'No attendance records available for the selected parameters.');
            return;
        }

        showLoading('Preparing Excel-compatible Report...');
        try {
            // Enhanced details for the report
            const headers = ["Student Name", "Email", "Date", "Overall Attendance %", "Daily Status", "Presence Type"];
            const rows = roster.map(s => [
                `"${s.name}"`,
                `"${s.email}"`,
                `"${selectedDate}"`,
                `"${s.attendance}%"`,
                `"${s.status}"`,
                `"${s.type}"`
            ]);

            const csvContent = headers.join(",") + "\n" + rows.map(r => r.join(",")).join("\n");

            // Using UTF-8 BOM to ensure Excel recognizes encoding immediately
            const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const filename = `Attendance_Report_${selectedDate}.csv`;
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();

            // Delayed cleanup to ensure download starts
            setTimeout(() => {
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
                URL.revokeObjectURL(url);
            }, 500);

            showSuccess('Export Successful', `Daily report "${filename}" has been generated.`);
        } catch (err) {
            console.error("Attendance Export Error:", err);
            showError('Export Failed', 'A system error occurred while generating the report.');
        } finally {
            closeLoading();
        }
    };

    const attendanceColumns = [
        {
            header: 'Candidate Identity', key: 'name', width: '30%', render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="avatar" style={{ width: '32px', height: '32px', fontSize: '12px' }}>{row.name.charAt(0)}</div>
                    <div>
                        <strong style={{ color: '#0F172A', display: 'block' }}>{row.name}</strong>
                        <span style={{ fontSize: '11px', color: '#64748B' }}>{row.email}</span>
                    </div>
                </div>
            )
        },
        {
            header: 'Sourcing Node', key: 'type', width: '20%', render: (row) => (
                <span style={{ fontSize: '13px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: row.type === 'Online' ? '#3B82F6' : '#94A3B8' }}></div>
                    {row.type === 'Online' ? 'Live Session (Auto)' : 'In-Person (Manual)'}
                </span>
            )
        },
        {
            header: 'Current Status', key: 'status', width: '20%', render: (row) => {
                let badgeClass = 'pending';
                if (row.status === 'Present') badgeClass = 'active';
                if (row.status === 'Absent') badgeClass = 'inactive';
                if (row.status === 'Late') badgeClass = 'pending';
                return <span className={`status-badge ${badgeClass}`} style={{ width: '70px', justifyContent: 'center' }}>{row.status}</span>;
            }
        },
        {
            header: 'Manual Override', key: 'actions', width: '30%', render: (row) => (
                <div style={{
                    display: 'inline-flex',
                    background: '#F1F5F9',
                    padding: '4px',
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    gap: '4px',
                    opacity: row.isEligible ? 1 : 0.6,
                    pointerEvents: row.isEligible ? 'auto' : 'none'
                }}>
                    <button
                        onClick={() => markAttendance(row.id, 'Present')}
                        title={!row.isEligible ? "Course Completed - Attendance Locked" : ""}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', cursor: row.isEligible ? 'pointer' : 'default', border: 'none', transition: 'all 0.2s',
                            background: row.status === 'Present' ? '#10B981' : 'transparent',
                            color: row.status === 'Present' ? 'white' : '#64748B',
                            boxShadow: row.status === 'Present' ? '0 2px 4px rgba(16,185,129,0.2)' : 'none'
                        }}
                    >
                        <FaUserCheck /> {row.status === 'Present' ? 'Present' : 'P'}
                    </button>
                    <button
                        onClick={() => markAttendance(row.id, 'Absent')}
                        title={!row.isEligible ? "Course Completed - Attendance Locked" : ""}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', cursor: row.isEligible ? 'pointer' : 'default', border: 'none', transition: 'all 0.2s',
                            background: row.status === 'Absent' ? '#EF4444' : 'transparent',
                            color: row.status === 'Absent' ? 'white' : '#64748B',
                            boxShadow: row.status === 'Absent' ? '0 2px 4px rgba(239,68,68,0.2)' : 'none'
                        }}
                    >
                        <FaUserTimes /> {row.status === 'Absent' ? 'Absent' : 'A'}
                    </button>
                    <button
                        onClick={() => markAttendance(row.id, 'Late')}
                        title={!row.isEligible ? "Course Completed - Attendance Locked" : ""}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', cursor: row.isEligible ? 'pointer' : 'default', border: 'none', transition: 'all 0.2s',
                            background: row.status === 'Late' ? '#F59E0B' : 'transparent',
                            color: row.status === 'Late' ? 'white' : '#64748B',
                            boxShadow: row.status === 'Late' ? '0 2px 4px rgba(245,158,11,0.2)' : 'none'
                        }}
                    >
                        <FaCalendarCheck /> {row.status === 'Late' ? 'Late' : 'L'}
                    </button>
                </div>
            )
        }
    ];

    if (loading && roster.length === 0) return <Loader />;

    const presentCount = roster.filter(s => s.status === 'Present').length;
    const totalCount = roster.length;

    return (
        <div className="dashboard-overview fade-in">
            {/* Header Control Center */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div>
                    <h1 className="page-title">Attendance Command Ledger</h1>
                    <p className="page-subtitle">Unified monitoring of online join logs and manual offline presence overrides.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="flex-stack" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <select
                                className="glass-input premium-select"
                                value={selectedCourseId}
                                onChange={(e) => setSelectedCourseId(e.target.value)}
                                style={{ minWidth: '220px', height: '44px', background: '#fff', border: '1.5px solid #E2E8F0', transition: 'none' }}
                            >
                                {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                            </select>
                            <input
                                type="date"
                                className="glass-input"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{ height: '44px', background: '#fff', border: '1.5px solid #E2E8F0' }}
                            />
                        </div>
                    </div>
                    <button className="secondary-btn pulse-on-hover" onClick={handleExport}>
                        <FaRegFileAlt style={{ marginRight: '8px' }} /> Daily Report
                    </button>
                    <button className="primary-btn pulse-on-hover" onClick={handleGenerateQR}>
                        <FaQrcode style={{ marginRight: '8px' }} /> Session QR
                    </button>
                </div>
            </div>

            {/* Attendance Analytics Grid */}
            <div className="kpi-grid" style={{ marginBottom: '32px' }}>
                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Batch Turnout Score</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaCalendarCheck /></div>
                    </div>
                    <h2 className="kpi-value">{totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0}%</h2>
                    <div className="kpi-trend trend-neutral">
                        <span>Synced with Live Studio</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Present Enrollees</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}><FaUserCheck /></div>
                    </div>
                    <h2 className="kpi-value">{presentCount} <span style={{ fontSize: '16px', color: '#64748B' }}>/ {totalCount}</span></h2>
                    <div className="kpi-trend trend-up">
                        <FaCheckCircle fontSize="10px" /> <span>Daily Engagement</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-header">
                        <span className="kpi-title">Absentee Alerts</span>
                        <div className="kpi-icon-wrapper" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}><FaUserTimes /></div>
                    </div>
                    <h2 className="kpi-value">{roster.filter(s => s.status === 'Absent').length}</h2>
                    <div className="kpi-trend trend-down">
                        <FaHistory fontSize="10px" /> <span>Historical Offset</span>
                    </div>
                </div>
            </div>

            {roster.length > 0 ? (
                <DataTable
                    title={`Batch Roster Ledger - ${new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                    columns={attendanceColumns}
                    data={roster.filter(s =>
                        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        s.email.toLowerCase().includes(searchTerm.toLowerCase())
                    )}
                    actions={
                        <div className="search-wrapper" style={{ width: '250px' }}>
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    }
                />
            ) : (
                <EmptyState
                    icon={FaRegCalendarCheck}
                    title="No Roster Available"
                    message="Either no students are enrolled in this course or no attendance session has been initiated for this date."
                />
            )}

            {/* Premium QR Modal - Aligned with LMS Styling */}
            {isQrModalOpen && createPortal(
                <div className={`modal-overlay ${isQrModalOpen ? 'open' : ''}`} onClick={() => setIsQrModalOpen(false)}>
                    <div className="modal-content glass-card fade-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2><FaQrcode style={{ marginRight: '12px', color: 'var(--primary-blue)' }} /> Session Sentinel QR</h2>
                            <button className="modal-close-btn" onClick={() => setIsQrModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body" style={{ padding: '32px', textAlign: 'center' }}>
                            <div style={{ background: 'white', padding: '24px', borderRadius: '24px', display: 'inline-block', boxShadow: '0 10px 40px rgba(0, 44, 95, 0.08)', marginBottom: '32px', border: '1px solid #E2E8F0' }}>
                                {/* High-tech Mock QR */}
                                <div style={{
                                    width: '240px', height: '240px',
                                    background: 'repeating-conic-gradient(var(--primary-blue) 0% 25%, #fff 0% 50%) 50% / 24px 24px',
                                    borderRadius: '16px', border: '12px solid var(--primary-blue)', position: 'relative'
                                }}>
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'var(--primary-gold)', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '16px', fontWeight: 900, letterSpacing: '2px', boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)' }}>ZENX</div>
                                </div>
                            </div>
                            <h3 style={{ color: '#0F172A', marginBottom: '12px', fontSize: '22px', fontWeight: 700 }}>Intelligent Batch Capture Active</h3>
                            <p style={{ color: '#64748B', fontSize: '15px', lineHeight: '1.7', marginBottom: '32px', padding: '0 10px' }}>
                                Ask in-person candidates to scan this dynamically encrypted token to commit their presence to the daily ledger. This session is securely hashed and verified.
                            </p>
                            <div className="modal-actions" style={{ justifyContent: 'center', borderTop: 'none', padding: 0 }}>
                                <button type="button" className="action-btn-gold pulse-on-hover" onClick={() => setIsQrModalOpen(false)} style={{ width: '100%', maxWidth: '320px', height: '52px', fontSize: '16px', borderRadius: '14px' }}>
                                    Lock Ledger & Close Capture <FaChevronRight style={{ marginLeft: '12px' }} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorAttendance;
